<template>
  <div
    class="vx-row">
    <div
      v-if="!compactDesign && showLabel && isLaptopOrDesktop"
      class="vx-col sm:w-1/5 w-full flex items-start pt-base">
      <label class="required">{{ fieldName }}</label>
    </div>
    <div
      class="vx-col w-full"
      :class="{'sm:w-4/5': !compactDesign && isLaptopOrDesktop}">
      <label
        v-if="showLabel"
        :class="['vs-input--label', compactDesign || !isLaptopOrDesktop ? 'required' : '']">
        {{ compactDesign || !isLaptopOrDesktop ? fieldName : ' ' }}
      </label>
      <v-select
        v-model="callerIdTypeLocal"
        class="w-full"
        :options="callerIdTypeOptions"
        :append-to-body="appendToBody"
        :clearable="false"
        :multiple="false"
        close-on-select
        :getOptionLabel="(option) => $t(`$Enums.Sender.Caller.Type.${option}`)"/>

      <div class="mt-3">
        <caller-business-name
          v-if="callerIdTypeLocal === $enums.Sender.Caller.Type.BUSINESS_NAME"
          ref="callerBusinessName"
          v-model="senderBusinessNameLocal"
          :input-name="fieldName"/>

        <v-select-dedicated-number
          v-if="callerIdTypeLocal === $enums.Sender.Caller.Type.DEDICATED_NUMBER"
          ref="vSelectDedicatedNumber"
          v-model="senderIdLocal"
          :on-modal="onModal"
          :field-name="fieldName"
          :append-to-body="appendToBody"
          @validate="(val) => senderDedicatedNumberComponentIsValid = val">
        </v-select-dedicated-number>

        <v-select-phone-number
          v-if="callerIdTypeLocal === $enums.Sender.Caller.Type.PHONE_NUMBER"
          ref="vSelectPhoneNumber"
          v-model="senderIdLocal"
          :append-to-body="appendToBody"
          @validate="(val) => senderPhoneNumberComponentIsValid = val">
        </v-select-phone-number>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import vSelect from 'vue-select';

import enums from '@/enums';

import VSelectPhoneNumber from '@/views/modules/campaigns/components/campaign-information/CampaignInformationVSelectPhoneNumber.vue';
import VSelectDedicatedNumber from '@/views/modules/campaigns/components/campaign-information/CampaignInformationVSelectDedicatedNumber.vue';
import CallerBusinessName from '@/views/modules/campaigns/components/campaign-information/CampaignInformationCallerIdBusinessName.vue';

export default {
  name: 'CampaignInformationFrom',
  components: {
    vSelect,
    CallerBusinessName,
    VSelectDedicatedNumber,
    VSelectPhoneNumber,
  },
  props: {
    value: {
      type: [Object, String],
      required: true,
    },
    callerIdType: {
      type: String,
      required: true,
      validator(type) {
        return Object.values(enums.Sender.Caller.Type).indexOf(type) !== -1;
      },
    },
    senderBusinessName: {
      type: String,
      required: true,
    },
    fieldName: {
      type: String,
      required: true,
    },
    compactDesign: {
      type: Boolean,
      required: false,
      default: false,
    },
    onModal: {
      type: Boolean,
      required: false,
      default: false,
    },
    showLabel: {
      type: Boolean,
      required: false,
      default: true,
    },
    appendToBody: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      callerIdTypeLocal: this.callerIdType,
      senderBusinessNameLocal: this.senderBusinessName,
      senderIdLocal: '',

      senderPhoneNumberComponentIsValid: false,
      senderDedicatedNumberComponentIsValid: false,

      callerIdTypeOptions: Object.values(this.$enums.Sender.Caller.Type).filter((type) => ![
        this.$enums.Sender.Caller.Type.PHONE_NUMBER,
        this.$enums.Sender.Caller.Type.BUSINESS_NAME,
      ].includes(type)),
    };
  },
  computed: {
    isLaptopOrDesktop() {
      return this.$mq.includes(this.$enums.mqBreakpoints.LAPTOP)
        || this.$mq.includes(this.$enums.mqBreakpoints.DESKTOP);
    },
    senderSharedNumberIsValid() {
      if (this.callerIdTypeLocal === this.$enums.Sender.Caller.Type.SHARED_NUMBER) {
        return !this.senderIdLocal && !this.senderBusinessNameLocal;
      }

      return null;
    },
    senderPhoneNumberIsValid() {
      if (this.callerIdTypeLocal === this.$enums.Sender.Caller.Type.PHONE_NUMBER) {
        return this.senderPhoneNumberComponentIsValid;
      }

      return null;
    },
    senderDedicatedNumberIsValid() {
      if (this.callerIdTypeLocal === this.$enums.Sender.Caller.Type.DEDICATED_NUMBER) {
        return this.senderDedicatedNumberComponentIsValid;
      }

      return null;
    },
    senderBusinessNameIsValid() {
      if (this.callerIdTypeLocal === this.$enums.Sender.Caller.Type.BUSINESS_NAME) {
        return !!this.senderBusinessNameLocal;
      }

      return null;
    },
    senderIdIsValid() {
      return this.senderSharedNumberIsValid
        || this.senderPhoneNumberIsValid
        || this.senderDedicatedNumberIsValid
        || this.senderBusinessNameIsValid
        || false;
    },
  },
  watch: {
    senderIdLocal(val) {
      this.$emit('input', val || '');
    },
    senderBusinessNameLocal(val) {
      this.$emit('update:sender-business-name', val);
    },
    callerIdTypeLocal(val) {
      this.senderIdLocal = '';
      this.senderBusinessNameLocal = '';
      this.$emit('update:caller-id-type', val);
    },
    senderIdIsValid: {
      immediate: true,
      handler(val) {
        this.$emit('is-valid', val);
      },
    },
  },
  created() {
    this.initSenderId();
  },
  methods: {
    ...mapActions({
      fetchAllCallerIds: 'callerId/fetchAllCallerIds',
      fetchCallerId: 'callerId/fetchCallerId',
    }),
    async initSenderId() {
      if (this.value) {
        switch (this.callerIdType) {
          case this.$enums.Sender.Caller.Type.DEDICATED_NUMBER:
          case this.$enums.Sender.Caller.Type.PHONE_NUMBER:
            if (typeof this.value === 'string') {
              this.senderIdLocal = await this.fetchCallerId(this.value);
              // eslint-disable-next-line no-underscore-dangle
            } else if (this.value && this.value._id) {
              // eslint-disable-next-line no-underscore-dangle
              this.senderIdLocal = await this.fetchCallerId(this.value._id);
            } else {
              this.senderIdLocal = this.value || '';
            }

            break;

          default:
            this.senderIdLocal = this.value || '';
        }
      } else {
        this.senderIdLocal = '';
      }
    },
    async validate() {
      let fromResult = true;

      switch (this.callerIdTypeLocal) {
        case this.$enums.Sender.Caller.Type.BUSINESS_NAME:
          fromResult = await this.$refs.callerBusinessName.$validator.validateAll();
          break;
        case this.$enums.Sender.Caller.Type.PHONE_NUMBER:
          fromResult = await this.$refs.vSelectPhoneNumber.validate();
          break;
        case this.$enums.Sender.Caller.Type.DEDICATED_NUMBER:
          fromResult = await this.$refs.vSelectDedicatedNumber.validate();
          break;

        default:
      }

      return fromResult;
    },
  },
};
</script>

<style scoped lang="scss">

</style>
